import { format } from 'date-fns';
import type { CoverPageData } from './pdfExportUtils';
import type { PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';

import { mapPackageTypeName } from './mapPackageTypeName';
import type { MainLayoutContextValue } from '../components/layouts/MainPageLayout/MainLayoutContext';

const imageToCanvas = (img: HTMLImageElement): string | undefined => {
  const MAX_SIZE = 800;
  let { naturalWidth: w, naturalHeight: h } = img;
  if (w > MAX_SIZE || h > MAX_SIZE) {
    const scale = MAX_SIZE / Math.max(w, h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  ctx.drawImage(img, 0, 0, w, h);
  try {
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return undefined;
  }
};

const loadImageViaFetch = async (url: string): Promise<string | undefined> => {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) return undefined;
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  return new Promise<string | undefined>((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve(imageToCanvas(img));
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(undefined);
    };
    img.src = objectUrl;
  });
};

const loadImageViaElement = (url: string): Promise<string | undefined> =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(imageToCanvas(img));
    img.onerror = () => resolve(undefined);
    img.src = url;
  });

const fetchImageAsDataUrl = async (url: string): Promise<string | undefined> => {
  try {
    const result = await loadImageViaFetch(url);
    if (result) return result;
  } catch {
    // fetch blocked by CORS — try img element fallback
  }
  try {
    return await loadImageViaElement(url);
  } catch {
    return undefined;
  }
};

export const buildCoverPageData = async (
  header: string,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
  regionsMapping: Record<number, string>,
  filteredProperties?: Array<PortfolioAnalyticsPropertyResponseDto>,
): Promise<CoverPageData> => {
  const coverPageFilters: CoverPageData['filters'] = [
    {
      label: 'Advertising',
      value: selectedFilters.package
        ? mapPackageTypeName(selectedFilters.package as Parameters<typeof mapPackageTypeName>[0])
        : 'All advertising',
    },
    {
      label: 'State',
      value: selectedFilters.stateRegionId
        ? (regionsMapping[selectedFilters.stateRegionId] ?? String(selectedFilters.stateRegionId))
        : 'All portfolio states',
    },
    {
      label: 'Metro area',
      value: selectedFilters.metropolitanServiceAreaRegionId
        ? (regionsMapping[selectedFilters.metropolitanServiceAreaRegionId] ??
          String(selectedFilters.metropolitanServiceAreaRegionId))
        : 'All metropolitan service areas',
    },
    {
      label: 'City',
      value: selectedFilters.cityRegionId
        ? (regionsMapping[selectedFilters.cityRegionId] ?? String(selectedFilters.cityRegionId))
        : 'All cities',
    },
    {
      label: 'ZIP code',
      value: selectedFilters.zipCodeRegionId
        ? (regionsMapping[selectedFilters.zipCodeRegionId] ?? String(selectedFilters.zipCodeRegionId))
        : 'All ZIP codes',
    },
  ];

  const isProperty = selectedFilters.listingAlias !== '';
  const selectedProperty = isProperty
    ? filteredProperties?.find((p) => p.propertyDetails.listingAlias === selectedFilters.listingAlias)
    : undefined;
  const rawImageUrl = selectedProperty?.propertyDetails.imageUrl || undefined;
  const propertyImageUrl = rawImageUrl ? await fetchImageAsDataUrl(rawImageUrl) : undefined;

  return {
    title: header,
    propertyName: isProperty ? (selectedProperty?.propertyDetails.listingName ?? null) : null,
    propertyImageUrl,
    date: format(new Date(), 'MMMM d, yyyy'),
    filters: coverPageFilters,
  };
};
